import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Custom property carrying the measured container width to the `.doc-root`
 * stylesheet rule in `WorkspaceDocumentStyles`.
 *
 * The width is handed to CSS rather than written straight to the element's
 * inline `width`, because an inline declaration would have to carry
 * `!important` to win on screen — and an inline `!important` outranks author
 * `!important` too, so the print stylesheets could no longer reset the width
 * for print (LAR-396).
 */
export const DOC_ZOOM_WIDTH_VAR = "--doc-zoom-width";

/**
 * Pin the base width and zoom factor on a `.doc-root` element.
 *
 * At zoom 100% the doc-root renders at exactly the container width; at 150% the
 * width is unchanged and CSS `zoom` scales it, so the content scales instead of
 * reflowing into the larger coordinate space.
 *
 * No-ops until the container has actually been measured — writing a 0px width
 * would collapse the document for a frame.
 */
export const applyDocumentZoom = (
  element: HTMLElement,
  baseWidth: number,
  zoomLevel: number
) => {
  if (baseWidth <= 0) return;
  element.style.setProperty(DOC_ZOOM_WIDTH_VAR, `${baseWidth}px`);
  element.style.zoom = String(zoomLevel / 100);
};

/**
 * Manages document zoom state and applies it to the `.doc-root` element.
 *
 * Tracks the container width via ResizeObserver and publishes it as
 * `--doc-zoom-width` so that CSS `zoom` visually scales the content rather than
 * re-flowing it to fill the zoomed coordinate space.
 *
 * @param rawHTML - The sanitised document HTML (used as a dependency signal for
 *   when the container is conditionally rendered).
 * @param documentKey - Identifies the document on screen. The hook stays mounted
 *   while the reader navigates to another document, so zoom resets to 100% when
 *   this changes — the same reset the legislation viewer does on `documentId`.
 *   Switching revision of the *same* document keeps the reader's zoom level
 *   instead; the pin is re-applied to the remounted doc-root.
 */
export const useDocumentZoom = (rawHTML: string, documentKey?: string) => {
  const [zoomLevel, setZoomLevel] = useState(100);
  const [baseWidth, setBaseWidth] = useState(0);
  /**
   * The discovered `.doc-root`, mirrored into state so the apply effect below
   * re-runs when DocumentRenderer remounts and replaces the node — switching
   * revision or document. The ref alone is
   * mutated in place, so an effect depending only on the zoom inputs left the
   * new node with no `--doc-zoom-width` and no `zoom`: the document rendered at
   * 100% while the toolbar still read the previous level.
   */
  const [docRoot, setDocRoot] = useState<HTMLElement | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const docRootRef = useRef<HTMLElement | null>(null);

  // "Fit to width" simply resets zoom to 100%
  const handleFitToWidth = useCallback(() => {
    setZoomLevel(100);
  }, []);

  // Track container width via ResizeObserver so we know the "base" render width.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setBaseWidth(entry.contentRect.width);
      }
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [rawHTML]);

  // A different document starts at 100%, so the zoom control never carries a
  // level over from the document the reader just left.
  useEffect(() => {
    setZoomLevel(100);
  }, [documentKey]);

  // Find the doc-root element after render
  useEffect(() => {
    const found =
      containerRef.current?.querySelector<HTMLElement>(".doc-root") ?? null;
    if (!found) return;
    docRootRef.current = found;
    setDocRoot((prev) => (prev === found ? prev : found));
  }, [rawHTML]);

  useEffect(() => {
    if (!docRoot) return;
    applyDocumentZoom(docRoot, baseWidth, zoomLevel);
  }, [docRoot, zoomLevel, baseWidth]);

  return {
    zoomLevel,
    setZoomLevel,
    handleFitToWidth,
    containerRef,
    docRootRef,
    docRootReady: docRoot !== null,
  };
};
